import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';

/**
 * Tracks the device's network connectivity status reactively.
 *
 * On native platforms (iOS/Android), uses the Capacitor `Network` plugin for
 * accurate reachability detection. On web, falls back to `navigator.onLine` with
 * `online`/`offline` window events.
 *
 * @returns `{ isOnline }` — `true` when the device has network connectivity.
 */
export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      Network.getStatus().then((status) => setIsOnline(status.connected));

      let listenerHandle: Awaited<ReturnType<typeof Network.addListener>>;

      Network.addListener('networkStatusChange', (status) => {
        setIsOnline(status.connected);
      }).then((handle) => {
        listenerHandle = handle;
      });

      return () => {
        listenerHandle?.remove();
      };
    } else {
      setIsOnline(navigator.onLine);

      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }
  }, []);

  return { isOnline };
}
